import React, { useContext } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { Container } from "@mantine/core";

import { useTranslation } from "react-i18next";
import { useForm } from "@mantine/form";
import { Button, TextInput, PasswordInput, Paper, Title, Text, Group, Stack } from "@mantine/core";
import { IconLock, IconCheck, IconX } from "@tabler/icons-react";
import { AppName } from "../../../Lib/data";
import { AllFatherContext } from "../../../App";
import { validatePassword } from "../../../Lib/methods";
import { PerformRequest } from "../../../Lib/usePerformRequest";
import "./styles.scss";
import LanguageSelector from "../../../Lib/i18n/LanguageSelector";

const CompleteResetPassword: React.FC = () => {
  const { token } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setLoading] = React.useState(false);
  const [status, setStatus] = React.useState<number | null>(null);

  const context = useContext(AllFatherContext);
  const form = useForm({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validate: {
      password: (value) => (validatePassword(value) ? null : t("auth.password") + " (min 8 chars)"),
      confirmPassword: (value, values) =>
        value === values.password ? null : t("auth.confirmPassword"),
    },
  });

  const handleSubmit = async () => {
    const { hasErrors } = form.validate();
    if (!hasErrors && token) {
      setLoading(true);
      setStatus(null);
      // TODO: Replace with real endpoint when available
      const r = await PerformRequest({
        method: "POST",
        route: `http://localhost:8080/student/auth/complete-reset`,
        data: { token, password: form.values.password },
        callback: () => setLoading(false),
      });
      setLoading(false);
      setStatus(r?.status);
    }
  };

  let cardContent = null;
  if (status === 200 || status === 201) {
    cardContent = (
      <Paper className="login-form" radius="lg" p="xl" withBorder>
        <Group justify="center" mb="md">
          <IconCheck size={40} color="#22c55e" />
        </Group>
        <Title order={3} ta="center" mb="xs">
          {t("auth.resetPassword.successTitle")}
        </Title>
        <Text ta="center" c="dimmed">
          {t("auth.resetPassword.successMessage")}
        </Text>
        <Button mt="lg" fullWidth radius="md" onClick={() => navigate("/login")}>
          {t("auth.resetPassword.backToLogin")}
        </Button>
      </Paper>
    );
  } else if (status === 400 || status === 404) {
    cardContent = (
      <Paper className="login-form" radius="lg" p="xl" withBorder>
        <Group justify="center" mb="md">
          <IconX size={40} color="#ef4444" />
        </Group>
        <Title order={3} ta="center" mb="xs">
          {t("auth.resetPassword.errorTitle")}
        </Title>
        <Text ta="center" c="dimmed">
          {t("auth.resetPassword.errorMessage")}
        </Text>
        <Button mt="lg" fullWidth radius="md" onClick={() => setStatus(null)}>
          {t("common.retry")}
        </Button>
      </Paper>
    );
  }

  return (
    <div className="login-page">
      <div style={{ position: "fixed", top: 24, right: 24, zIndex: 2000 }}>
        <LanguageSelector />
      </div>
      <div className="background-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>
      <Container size="sm" className="login-container">
        <div className="login-content">
          <div className="logo-section">
            <img src={context?.assets?.logos?.legacy} alt={AppName} className="logo" />
            <Title order={1} className="app-title">
              {AppName}
            </Title>
            <Text className="welcome-text" size="lg" c="dimmed">
              {t("auth.resetPassword.title")}
            </Text>
          </div>
          {cardContent ? (
            cardContent
          ) : (
            <Paper className="login-form" radius="lg" p="xl" withBorder>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                <Stack gap="lg">
                  <Text ta="center" c="dimmed" mb="sm">
                    {t("auth.resetPassword.completeSubtitle")}
                  </Text>
                  <PasswordInput
                    leftSection={<IconLock size={16} />}
                    label={t("auth.password")}
                    placeholder={t("auth.password")}
                    size="md"
                    radius="md"
                    variant="filled"
                    {...form.getInputProps("password")}
                    classNames={{
                      input: "form-input",
                      label: "form-label",
                    }}
                  />
                  <PasswordInput
                    leftSection={<IconLock size={16} />}
                    label={t("auth.confirmPassword")}
                    placeholder={t("auth.confirmPassword")}
                    size="md"
                    radius="md"
                    variant="filled"
                    {...form.getInputProps("confirmPassword")}
                    classNames={{
                      input: "form-input",
                      label: "form-label",
                    }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    size="md"
                    radius="md"
                    loading={isLoading}
                    className="login-button"
                  >
                    {t("common.submit")}
                  </Button>
                  <Button variant="subtle" fullWidth mt="xs" onClick={() => navigate("/login")}>
                    {t("auth.resetPassword.backToLogin")}
                  </Button>
                </Stack>
              </form>
            </Paper>
          )}
        </div>
      </Container>
    </div>
  );
};

export default CompleteResetPassword;
